/*
  pdp-sticky-atc.js
  WHAT: Always-on sticky ATC bar behaviour (all viewports):
          - Variant change in the main form mirrors to the sticky bar (price,
            variant title, image, ATC label/disabled).
          - Clicking the sticky ATC calls form.requestSubmit() on the main PDP
            form so Shopify's standard add-to-cart flow runs.
        The bar is part of the page chrome from first paint; no scroll-gated
        reveal. CSS handles position/visibility.
  WHY:  im8health reference + user feedback "add to cart bar the whole time."
        Conversion safety net — ATC reachable from any scroll position.
  WHEN: Added 2026-05-28 (Step 8 — IntersectionObserver-gated, mobile-only) →
        revised 2026-05-29 (always-on, all viewports, IntersectionObserver removed).
*/

(function () {
  'use strict';

  var sticky = document.querySelector('[data-pdp-sticky]');
  if (!sticky) return;

  var buyboxSection = document.querySelector('.ba-pdp-buybox');
  if (!buyboxSection) return;

  var mainAtc = buyboxSection.querySelector('[data-pdp-atc]');
  var mainForm = buyboxSection.querySelector('[data-pdp-form]');
  var stickyAtc = sticky.querySelector('[data-pdp-sticky-atc]');

  // Body padding so content isn't covered by the always-visible sticky bar.
  document.body.classList.add('has-pdp-sticky');

  // Bar is part of the page chrome from first paint (im8health-style always-on).
  // No IntersectionObserver gating — the original "reveal when main ATC scrolls
  // off" treatment was removed 2026-05-29 because the bar should reinforce the
  // ATC throughout the scroll, not only on long PDPs past the buybox.
  sticky.setAttribute('aria-hidden', 'false');

  // ─── Click sticky ATC → submit the main form ────────────────────────────
  if (stickyAtc && mainForm) {
    stickyAtc.addEventListener('click', function (e) {
      e.preventDefault();
      if (stickyAtc.disabled) return;
      // Prefer requestSubmit so any "submit" listeners on the main ATC still fire.
      if (typeof mainForm.requestSubmit === 'function') {
        mainForm.requestSubmit(mainAtc || undefined);
      } else {
        mainForm.submit();
      }
    });
  }

  // ─── Sticky colour swatch → drive the REAL buybox radio ─────────────────
  /*
    Added 2026-09-21 (owner, Bose sticky-footer reference). The swatches in the bar
    are buttons, not radios, and deliberately hold no variant state of their own.

    The ATC above submits the main PDP form, so the form's own radios must remain the
    single source of truth — a parallel state here could disagree with them and post a
    colour the shopper never chose. Clicking a sticky swatch therefore checks the
    matching buybox radio and fires a bubbling change, which runs pdp-buybox.js and
    then the mirror below. Everything the bar shows, including which swatch is active,
    comes back through that one path.
  */
  var swatchGroup = sticky.querySelector('[data-pdp-sticky-swatches]');

  function syncStickySwatches() {
    if (!swatchGroup) return;
    var checked = buyboxSection.querySelector('[data-pdp-variant-input]:checked');
    if (!checked) return;
    var current = checked.value;
    swatchGroup.querySelectorAll('[data-pdp-sticky-swatch]').forEach(function (btn) {
      var active = btn.getAttribute('data-value') === current;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  if (swatchGroup) {
    swatchGroup.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pdp-sticky-swatch]');
      if (!btn) return;
      var value = btn.getAttribute('data-value');
      if (!value) return;

      var radios = buyboxSection.querySelectorAll('[data-pdp-variant-input]');
      var target = null;
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === value) { target = radios[i]; break; }
      }
      // No matching radio means the buybox and the bar have drifted; do nothing
      // rather than guess, so the form can never post an unintended variant.
      if (!target || target.checked) return;

      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // ─── Mirror variant changes from the main form to the sticky bar ────────
  buyboxSection.addEventListener('change', function (e) {
    var input = e.target.closest('[data-pdp-variant-input]');
    if (!input) return;
    syncStickySwatches();
    // Defer one tick so pdp-buybox.js has updated the buybox first.
    setTimeout(function () {
      // Hidden id field is the source of truth post-update.
      var idField = buyboxSection.querySelector('[data-pdp-variant-id]');
      var jsonNode = buyboxSection.querySelector('[data-pdp-product-json]');
      if (!idField || !jsonNode) return;
      var productData;
      try { productData = JSON.parse(jsonNode.textContent); } catch (err) { return; }
      var variant = productData.variants.find(function (v) { return String(v.id) === String(idField.value); });
      if (!variant) return;

      /*
        Was-price + percentage badge only. The now-price is deliberately NOT here any
        more (owner 2026-08-26): it is on the ATC button, and stating it twice in one
        bar was the thing being cleaned up.
      */
      var wasEl = sticky.querySelector('[data-pdp-sticky-was]');
      var saveEl = sticky.querySelector('[data-pdp-sticky-save]');
      /*
        Counterpart to snippets/ba-price.liquid, which strips the currency code from
        Liquid-rendered prices. This strips it here too.

        Why it matters: this shop's money format is `AUD ${{amount}}`. Today
        window.Shopify.money_format happens to be undefined on this theme, so the
        fallback below produced "$169" and the JS looked correct by luck. If that
        global ever gets defined, every price this handler writes would silently gain
        "AUD " back and the page would disagree with itself again — the exact bug the
        owner reported, only inverted. Stripping an ISO code is safe: the remainder of
        a money format is digits and symbols.
      */
      var formatMoney = function (cents) {
        var dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
        var fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
        return fmt
          .replace(/\{\{\s*amount\s*\}\}/, dollars)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\b[A-Z]{3}\b\s*/g, '')
          .trim();
      };
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (wasEl) {
          wasEl.innerHTML = '<s>' + formatMoney(variant.compare_at_price) + '</s>';
          wasEl.hidden = false;
        }
        if (saveEl) {
          // Dollar saving, matching the Liquid and the buybox (owner 2026-08-26).
          saveEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
          saveEl.hidden = false;
        }
      } else {
        if (wasEl) wasEl.hidden = true;
        if (saveEl) saveEl.hidden = true;
      }

      // Variant title (after the product name dot).
      var variantSpan = sticky.querySelector('[data-pdp-sticky-variant]');
      if (variantSpan) variantSpan.textContent = variant.title;

      /*
        Thumb image. Owner 2026-08-25: "in the sticky cart when the variant is changed
        the name is changing but the image is not."

        Cause: the Liquid renders this <img> through `image_tag` with widths, so it
        carries a SRCSET. When a srcset is present the browser picks its source from
        there and IGNORES src, so assigning src alone changed nothing — which is
        exactly why the name updated and the picture did not.

        So the srcset is rebuilt from the variant's image at the same widths the Liquid
        emits, rather than dropped: dropping it would leave a single fixed source and
        lose the retina candidate on a bar that is visible the whole way down the page.
      */
      if (variant.featured_image && variant.featured_image.src) {
        var thumbImg = sticky.querySelector('[data-pdp-sticky-thumb]');
        if (thumbImg) {
          /*
            Owner 2026-08-26: "the image loading in the sticky footer seems to be very
            delayed. we will need to check the code to ensure it is an instant change."

            It was a NETWORK wait, not a code delay. Rebuilding the srcset asks for
            `?width=120` of the new variant's image - a URL the browser has never
            fetched - so the bar kept showing the OLD picture until that round trip
            finished. The main gallery looked instant by comparison because it fetches
            nothing: every media item is already in the DOM and it only toggles which
            one is active.

            So the thumb now borrows the picture the gallery has ALREADY loaded for
            that variant. Same URL means the memory cache answers, and the swap paints
            on the same frame as the variant change. It costs no extra request, which
            also means no new weight on a bar that is on screen the whole page.

            srcset has to be removed when doing this: a present srcset wins over src,
            which is the same trap that stopped the image updating at all on 08-25.
            The gallery source is far larger than the 48px box, so there is no retina
            loss - the browser downscales an image it has already decoded.
          */
          var mediaId = (variant.featured_media && variant.featured_media.id) ||
                        (variant.featured_image && variant.featured_image.id) || null;
          var galleryImg = mediaId
            ? document.querySelector('[data-pdp-media-item][data-media-id="' + mediaId + '"] img')
            : null;
          var cached = galleryImg && (galleryImg.currentSrc || galleryImg.getAttribute('src'));

          if (cached) {
            thumbImg.removeAttribute('srcset');
            thumbImg.removeAttribute('sizes');
            thumbImg.src = cached;
          } else {
            /* No gallery match (a variant whose image is not in the media stack).
               Fall back to building the small source, and accept the fetch. */
            var base = variant.featured_image.src;
            var sep = base.indexOf('?') === -1 ? '?' : '&';
            var widths = [60, 90, 120];
            thumbImg.srcset = widths.map(function (w) {
              return base + sep + 'width=' + w + ' ' + w + 'w';
            }).join(', ');
            thumbImg.src = base + sep + 'width=120';
          }
          thumbImg.alt = variant.featured_image.alt || productData.title || '';
        }
      }

      // ATC label + disabled state.
      if (stickyAtc) {
        stickyAtc.disabled = !variant.available;
        var label = stickyAtc.querySelector('[data-pdp-sticky-atc-label]');
        if (label) {
          // textContent alone would wipe the price span the Liquid renders, so the
          // label is rebuilt the same way pdp-buybox.js rebuilds the main ATC.
          if (variant.available) {
            label.innerHTML = 'Add to cart · <span data-pdp-sticky-atc-price>' +
              formatMoney(variant.price) + '</span>';
          } else {
            label.textContent = 'Sold out';
          }
        }
      }
    }, 0);
  });
})();
